import axios, { AxiosError } from 'axios';
import { FC, FormEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import { PageHeading } from '@/components/Layout/Parts';
import Text from '@/components/Text';
import useToasts from '@/hooks/useToasts';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

const Form = styled.form`
  border: 1px solid rgba(242, 243, 247, 1);
  border-radius: 4px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.white};
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 500px;
`;

const Footer = styled(Larger)`
  display: flex;
  justify-content: flex-end;
`;

type FormDataT = {
  file: File | null;
};

const FacebookVideoStories: FC = () => {
  const [errors, setErrors] = useState<Array<{ row: number; errors: string }>>(
    [],
  );

  const toasts = useToasts();

  const {
    control,
    formState: { isSubmitting },
    setError,
    clearErrors,
    handleSubmit,
  } = useForm<FormDataT>({
    defaultValues: {
      file: null,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const formData = new FormData();

        if (data.file) {
          formData.append('file', data.file);
        }

        await api.post('/v1/editorial/facebook-video-stories', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        toasts.create('success', 'Import successful!');
      } catch (err) {
        toasts.create('error', 'Operation failed!');
        handleFormErrors(err, setError);
        if (axios.isAxiosError(err)) {
          const error = err as AxiosError<{
            errors: Array<{ row: number; errors: string }>;
          }>;

          setErrors(error.response?.data?.errors ?? []);
        }
      }
    })(e);
  };
  return (
    <>
      <Text $type="caption" $color="gray">
        EDITORIAL
      </Text>
      <PageHeading>Facebook Video Stories</PageHeading>

      <Form onSubmit={onSubmit}>
        <FormLayout>
          <Larger>
            <Text $type="caption">
              Import and distribute video stories from CSV
            </Text>
            <Text $type="smallMedium">Required columns: video, page_id</Text>
          </Larger>
          <FormField<FormDataT>
            id="csv-file-input"
            type="file"
            accept="text/csv"
            control={control}
            notes="Format: CSV"
            name="file"
          />
          {!!errors.length && (
            <Larger>
              {errors.map((err, i) => (
                <ValidationMessage key={i}>
                  Row {err.row}: {err.errors}
                </ValidationMessage>
              ))}
            </Larger>
          )}
          <Footer>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              Upload
            </Button>
          </Footer>
        </FormLayout>
      </Form>
    </>
  );
};

export default FacebookVideoStories;
